import { Component } from '@theme/component';
import { LastVisitedCollection } from '@theme/last-visited-collection';

/**
 * @typedef {Object} BreadcrumbTrailRefs
 * @property {HTMLLIElement} [collectionItem] - The collection crumb list item.
 * @property {HTMLLIElement} [collectionSeparator] - The separator before the collection crumb.
 * @property {HTMLAnchorElement} [collectionLink] - The collection crumb link.
 */

/**
 * Swaps the PDP breadcrumb's collection crumb for the PLP the shopper came from.
 * Keeps the server-rendered fallback when there is no same-site PLP to use.
 *
 * @extends {Component<BreadcrumbTrailRefs>}
 */
class BreadcrumbTrail extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#applyLastVisitedCollection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyLastVisitedCollection();
  }

  #applyLastVisitedCollection() {
    const { collectionItem, collectionSeparator, collectionLink } = this.refs;
    if (!collectionItem || !collectionSeparator || !collectionLink) return;
    if (this.dataset.scopedCollection === 'true') return;

    const visit = LastVisitedCollection.get();
    if (!visit) return;

    const visitUrl = new URL(visit.url, window.location.origin);
    if (visitUrl.origin !== window.location.origin) return;
    if (!this.#cameFrom(visitUrl)) return;

    const renderedHref = collectionLink.getAttribute('href') ?? '';
    const isSameCollection = renderedHref === visitUrl.pathname && !collectionItem.hidden;
    if (isSameCollection) return;

    collectionLink.href = visitUrl.pathname;
    collectionLink.textContent = visit.title;
    collectionItem.hidden = false;
    collectionSeparator.hidden = false;
  }

  /**
   * Only the PLP itself counts, so a product opened from the homepage or another PDP
   * keeps the server-rendered crumb instead of a stale collection from earlier in the tab.
   * @param {URL} collectionUrl - The stored collection page.
   * @returns {boolean} True when the shopper arrived from that collection page.
   */
  #cameFrom(collectionUrl) {
    if (!document.referrer) return false;

    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === collectionUrl.origin && referrer.pathname === collectionUrl.pathname;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('breadcrumb-trail')) {
  customElements.define('breadcrumb-trail', BreadcrumbTrail);
}
